import { Drawer, Flex, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import "../../assets/scss/auditlog.scss";
import {
	getAuditLogList,
	getAuditLogListV2,
} from "../../services/api-server/audit_logs";
import { GetAntIcon } from "../../utils/ant_icons";
import SearchButton from "../SearchButton";

interface AuditLogModalProps {
	tenantKey?: string;
	open: boolean;
	type: string;
	label: string;
	handleClose: () => void;
	identifierKeys?: any;
	auditlogFilter?: any;
}

function AuditLogModal(props: AuditLogModalProps) {
	const tenantKey = props.tenantKey;
	const open = props.open;
	const type = props.type;
	const label =
		props.label || props.type.charAt(0).toUpperCase() + props.type.slice(1);
	const handleClose = props.handleClose;
	const identifierKeys = props.identifierKeys;
	const auditlogFilter: any = props.auditlogFilter;

	const [auditData, setAuditData] = useState<any>([]);
	const [filteredDataTable, setFilteredDataTable] = useState<any>(null);
	const [tempRecord, setTempRecord] = useState<any>(null);
	const [searchRecord, setSearchRecord] = useState<any>([]);

	useEffect(() => {
		const moduleReadyForV2 = ["lesson_learned", "reporting"];
		if (!open) {
			setAuditData([]);
			return;
		}
		if (moduleReadyForV2.includes(type)) {
			getAuditLogListV2(auditlogFilter, tenantKey)
				.then((data: any) => {
					setAuditData(data || []);
				})
				.catch((error: any) => {
					console.log(error);
				});
		} else {
			getAuditLogList(tenantKey, identifierKeys, type, auditlogFilter)
				.then((data: any) => {
					setAuditData(data || []);
				})
				.catch((error: any) => {
					console.log(error);
				});
		}
	}, [type, identifierKeys, auditlogFilter, open]);

	const handleSearch = (record: any) => {
		setTempRecord(record);
	};

	useEffect(() => {
		if (searchRecord && tempRecord) {
			let recordIndex = searchRecord.findIndex((item: any) => {
				return item.columnKey === tempRecord.columnKey;
			});
			if (recordIndex !== -1) {
				if (tempRecord.value != "") {
					let _record = [...searchRecord];
					_record[recordIndex] = tempRecord;
					setSearchRecord(_record);
				} else {
					let _record = [...searchRecord];
					_record.splice(recordIndex, 1);
					setSearchRecord(_record);
				}
			} else {
				if (tempRecord.value != "") {
					let _record = [...searchRecord];
					_record.push(tempRecord);
					setSearchRecord(_record);
				}
			}
		}
	}, [tempRecord]);

	useEffect(() => {
		if (searchRecord?.length > 0) {
			let _data = [...auditData];
			const filteredData = _data?.filter((item: any) => {
				return searchRecord.every((record: any) => {
					if (Array.isArray(item[record.columnKey])) {
						return item[record.columnKey].some((arrayItem: any) => {
							if (typeof arrayItem === "object") {
								return Object.values(arrayItem).some((value: any) => {
									if (Array.isArray(value)) {
										return value.some((innerValue) =>
											innerValue
												?.toString()
												.toLowerCase()
												.includes(record.value.toString().toLowerCase())
										);
									} else {
										return value
											?.toString()
											.toLowerCase()
											.includes(record.value.toString().toLowerCase());
									}
								});
							} else {
								return arrayItem
									.toString()
									.toLowerCase()
									.includes(record.value.toString().toLowerCase());
							}
						});
					} else {
						return item[record.columnKey]
							.toString()
							.toLowerCase()
							.includes(record.value.toString().toLowerCase());
					}
				});
			});
			setFilteredDataTable(filteredData);
		} else {
			setFilteredDataTable(null);
		}
	}, [searchRecord]);

	const renderObjectFields = (obj: any, isStrikethrough: boolean = false) => {
		if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
			return null;
		}

		const fields = Object.entries(obj)
			.filter(
				([_, value]) => value !== null && value !== undefined && value !== ""
			)
			.map(([key, value]) => {
				let displayValue: any;
				if (Array.isArray(value)) {
					displayValue = displayArray(value, isStrikethrough);
				} else if (typeof value === "object") {
					displayValue = renderObjectFields(value, isStrikethrough);
				} else {
					displayValue = value;
				}

				return (
					<div key={key} style={{ marginLeft: "8px" }}>
						{key}: {displayValue}
					</div>
				);
			});

		return <div>{fields}</div>;
	};

	const displayArray = (data: any, isStrikethrough: boolean = false) => {
		if (!Array.isArray(data)) {
			return null;
		}

		return (
			<ul style={{ margin: 0, paddingLeft: "20px" }}>
				{data.map((item: any, index: number) => {
					if (item === null || item === undefined || item === "") {
						return null;
					}

					let content: any;
					if (typeof item === "object" && !Array.isArray(item)) {
						// Object in array - display its fields
						const fields = Object.entries(item)
							.filter(
								([_, value]) =>
									value !== null && value !== undefined && value !== ""
							)
							.map(([key, value]) => {
								let displayValue: any;
								if (Array.isArray(value)) {
									displayValue = displayArray(value, isStrikethrough);
								} else if (typeof value === "object") {
									displayValue = renderObjectFields(value, isStrikethrough);
								} else {
									displayValue = value;
								}
								return (
									<div key={key} style={{ marginLeft: "8px" }}>
										{key}: {displayValue}
									</div>
								);
							});
						content = <div>{fields}</div>;
					} else if (Array.isArray(item)) {
						content = displayArray(item, isStrikethrough);
					} else {
						content = item;
					}

					return (
						<li key={index} style={{ marginBottom: "4px" }}>
							{isStrikethrough ? (
								<Typography.Text delete>{content}</Typography.Text>
							) : (
								content
							)}
						</li>
					);
				})}
			</ul>
		);
	};

	const displayArrayWithComparison = (newData: any, oldData: any) => {
		if (!Array.isArray(newData) && !Array.isArray(oldData)) {
			return null;
		}

		const newArray = Array.isArray(newData) ? newData : [];
		const oldArray = Array.isArray(oldData) ? oldData : [];

		return (
			<ul style={{ margin: 0, paddingLeft: "20px" }}>
				{/* Display all items from new_data */}
				{newArray.map((item: any, index: number) => {
					if (item === null || item === undefined || item === "") {
						return null;
					}

					let content: any;
					if (typeof item === "object" && !Array.isArray(item)) {
						const fields = Object.entries(item)
							.filter(
								([_, value]) =>
									value !== null && value !== undefined && value !== ""
							)
							.map(([key, value]) => {
								let displayValue: any;
								if (Array.isArray(value)) {
									displayValue = displayArray(value, false);
								} else if (typeof value === "object") {
									displayValue = renderObjectFields(value, false);
								} else {
									displayValue = value;
								}
								return (
									<div key={key} style={{ marginLeft: "8px" }}>
										{key}: {displayValue}
									</div>
								);
							});
						content = <div>{fields}</div>;
					} else if (Array.isArray(item)) {
						content = displayArray(item, false);
					} else {
						content = item;
					}

					return (
						<li key={`new-${index}`} style={{ marginBottom: "4px" }}>
							{content}
						</li>
					);
				})}
				{/* Display all items from old_data with strikethrough */}
				{oldArray.map((item: any, index: number) => {
					if (item === null || item === undefined || item === "") {
						return null;
					}

					let content: any;
					if (typeof item === "object" && !Array.isArray(item)) {
						const fields = Object.entries(item)
							.filter(
								([_, value]) =>
									value !== null && value !== undefined && value !== ""
							)
							.map(([key, value]) => {
								let displayValue: any;
								if (Array.isArray(value)) {
									displayValue = displayArray(value, true);
								} else if (typeof value === "object") {
									displayValue = renderObjectFields(value, true);
								} else {
									displayValue = value;
								}
								return (
									<div key={key} style={{ marginLeft: "8px" }}>
										{key}: {displayValue}
									</div>
								);
							});
						content = <div>{fields}</div>;
					} else if (Array.isArray(item)) {
						content = displayArray(item, true);
					} else {
						content = item;
					}

					return (
						<li key={`old-${index}`} style={{ marginBottom: "4px" }}>
							<Typography.Text delete>{content}</Typography.Text>
						</li>
					);
				})}
			</ul>
		);
	};

	const renderObjectComparison = (newData: any, oldData: any) => {
		if (!newData || typeof newData !== "object" || Array.isArray(newData)) {
			return null;
		}

		const allKeys = new Set([
			...(oldData ? Object.keys(oldData) : []),
			...Object.keys(newData),
		]);

		const fields = Array.from(allKeys)
			.filter((key) => {
				const newVal = newData[key];
				const oldVal = oldData?.[key];
				// Only show if there's a change or if it's new/removed
				return (
					(newVal !== null && newVal !== undefined && newVal !== "") ||
					(oldVal !== null && oldVal !== undefined && oldVal !== "")
				);
			})
			.map((key) => {
				const newVal = newData[key];
				const oldVal = oldData?.[key];

				// Skip if values are the same
				if (JSON.stringify(newVal) === JSON.stringify(oldVal)) {
					return null;
				}

				let newDisplay: any;
				let oldDisplay: any;

				if (Array.isArray(newVal)) {
					newDisplay = displayArray(newVal, false);
				} else if (typeof newVal === "object" && newVal !== null) {
					newDisplay = renderObjectFields(newVal, false);
				} else {
					newDisplay = newVal;
				}

				if (oldVal !== null && oldVal !== undefined && oldVal !== "") {
					if (Array.isArray(oldVal)) {
						oldDisplay = displayArray(oldVal, true);
					} else if (typeof oldVal === "object") {
						oldDisplay = renderObjectFields(oldVal, true);
					} else {
						oldDisplay = oldVal;
					}
				}

				return (
					<div key={key} style={{ marginBottom: "4px" }}>
						{key}: {newDisplay}{" "}
						{oldDisplay && (
							<Typography.Text delete style={{ marginLeft: "4px" }}>
								{oldDisplay}
							</Typography.Text>
						)}
					</div>
				);
			})
			.filter((item) => item !== null);

		return <div>{fields}</div>;
	};

	return (
		<Drawer
			title={`Audit log - ${label} details`}
			open={open}
			closeIcon={GetAntIcon("rightarrow")}
			onClose={handleClose}
			width={"50vw"}
			zIndex={9999}
		>
			<div>
				<Table
					// rootClassName="audit-log-table"
					style={{ width: "100%" }}
					bordered={true}
					dataSource={filteredDataTable || auditData}
					columns={[
						{
							title: "Date",
							dataIndex: "created_at",
							key: "Created date",
							width: 200,
							render: (value: any) => {
								return dayjs(value).format("DD MMM YYYY HH:mm:ss");
							},
							sorter: (a: any, b: any) => {
								let dateA = new Date(a.created_at);
								let dateB = new Date(b.created_at);
								if (dateA < dateB) return -1;
								else if (dateA > dateB) return 1;
								else return 0;
							},
						},
						{
							title: (
								<SearchButton
									// buttonType="link"
									handleSearch={handleSearch}
									headerName="User"
									columnKey="created_by"
									key={"createdByx"}
								/>
							),
							dataIndex: "created_by",
							key: "created_by",
							width: 200,
						},
						{
							title: (
								<SearchButton
									// buttonType="link"
									handleSearch={handleSearch}
									headerName="Event"
									columnKey="event"
									key={"event"}
								/>
							),
							dataIndex: "event",
							key: "event name",
							width: 200,
						},
						{
							title: (
								<SearchButton
									// buttonType="link"
									handleSearch={handleSearch}
									headerName="Details"
									columnKey="details"
									key={"key" + "details"}
								/>
							),
							dataIndex: "details",
							key: "event",
							width: 200,
							render: (value: any) => {
								return value?.map((item: any, index: number) => {
									const hasNewData =
										item?.new_data !== null &&
										item?.new_data !== undefined &&
										item?.new_data !== "";
									const hasOldData =
										item?.old_data !== null &&
										item?.old_data !== undefined &&
										item?.old_data !== "";

									// Skip if no data to display
									if (!hasNewData && !hasOldData) {
										return null;
									}

									// Handle object comparison (field by field)
									const isNewDataObject =
										hasNewData &&
										typeof item?.new_data === "object" &&
										!Array.isArray(item?.new_data) &&
										item?.new_data !== null;
									const isOldDataObject =
										hasOldData &&
										typeof item?.old_data === "object" &&
										!Array.isArray(item?.old_data) &&
										item?.old_data !== null;

									// If both are objects, use comparison view
									if (isNewDataObject && isOldDataObject) {
										return (
											<Flex
												key={index}
												style={{ marginBottom: "16px" }}
												gap={4}
												vertical
											>
												{item?.event && (
													<Tag
														className="event-tag"
														style={{
															whiteSpace: "normal",
															wordWrap: "break-word",
															borderRadius: "2px",
															border: "1px solid rgba(255,255,255,0.3)",
															background: "rgba(113, 113, 113, 0.50)",
															color: "rgba(255, 255, 255, 0.85",
															margin: "2px",
														}}
													>
														<span
															dangerouslySetInnerHTML={{
																__html: item.event,
															}}
														/>
													</Tag>
												)}
												<Typography.Text>
													{renderObjectComparison(
														item?.new_data,
														item?.old_data
													)}
												</Typography.Text>
											</Flex>
										);
									}

									// Handle arrays or simple values
									const isNewDataArray = Array.isArray(item?.new_data);
									const isOldDataArray = Array.isArray(item?.old_data);

									// If both are arrays, use combined display
									if (isNewDataArray && isOldDataArray) {
										return (
											<Flex
												key={index}
												style={{ marginBottom: "16px" }}
												gap={4}
												vertical
											>
												{item?.event && (
													<Tag
														className="event-tag"
														style={{
															whiteSpace: "normal",
															borderRadius: "2px",
															border: "1px solid rgba(255,255,255,0.3)",
															background: "rgba(113, 113, 113, 0.50)",
															color: "rgba(255, 255, 255, 0.85",
															margin: "2px",
														}}
													>
														<span
															dangerouslySetInnerHTML={{
																__html: item.event,
															}}
														/>
													</Tag>
												)}
												<Typography.Text>
													{displayArrayWithComparison(
														item?.new_data,
														item?.old_data
													)}
												</Typography.Text>
											</Flex>
										);
									}

									return (
										<Flex
											key={index}
											style={{ marginBottom: "16px" }}
											gap={4}
											vertical
										>
											{item?.event && (
												<Tag
													className="event-tag"
													style={{
														whiteSpace: "normal",
														borderRadius: "2px",
														border: "1px solid rgba(255,255,255,0.3)",
														background: "rgba(113, 113, 113, 0.50)",
														color: "rgba(255, 255, 255, 0.85",
														margin: "2px",
													}}
												>
													<span
														dangerouslySetInnerHTML={{
															__html: item.event,
														}}
													/>
												</Tag>
											)}
											{hasNewData && (
												<Typography.Text>
													{Array.isArray(item?.new_data) ? (
														displayArray(item?.new_data, false)
													) : typeof item?.new_data === "object" &&
													  item?.new_data !== null ? (
														renderObjectFields(item?.new_data, false)
													) : (
														<span
															dangerouslySetInnerHTML={{
																__html: String(item?.new_data),
															}}
														/>
													)}
												</Typography.Text>
											)}
											{hasOldData && (
												<Typography.Text delete>
													{Array.isArray(item?.old_data) ? (
														displayArray(item?.old_data, true)
													) : typeof item?.old_data === "object" &&
													  item?.old_data !== null ? (
														renderObjectFields(item?.old_data, true)
													) : (
														<span
															dangerouslySetInnerHTML={{
																__html: String(item?.old_data),
															}}
														/>
													)}
												</Typography.Text>
											)}
										</Flex>
									);
								});
							},
						},
					]}
				/>
			</div>
		</Drawer>
	);
}

export default AuditLogModal;
